import LanguageSwitcher from '../../i18n/LanguageSwitcher';
import {localizePath, t, useSiteLanguage} from "../../i18n";
import {useContext, useEffect, useId, useLayoutEffect, useRef} from "react";
import {createPortal} from 'react-dom';
import {NavbarContext} from "../../context/NavbarContext";
import {Link, NavLink} from "react-router";
import restaurantInfo from "../../data/restaurant.json";
import SocialLinks from "../socialLinks/SocialLinks";
import ToggleMenu from "../../assets/icons/toggleMenu";
import Close from "../../assets/icons/close";
import ReservationButton from "../reservation/ReservationButton";
import './navbar.css';


const Navbar = () => {
    useSiteLanguage();

    const { navbarOpen, setNavbarOpen } = useContext(NavbarContext);
    const menuId = useId();
    const menuRef = useRef<HTMLDivElement>(null);
    const toggleRef = useRef<HTMLButtonElement>(null);

    useLayoutEffect(() => {
        menuRef.current?.toggleAttribute('inert', !navbarOpen);
    }, [navbarOpen]);

    useEffect(() => {
        const desktop = window.matchMedia('(min-width: 900px)');
        const closeOnDesktop = () => {
            if (desktop.matches) setNavbarOpen(false);
        };
        closeOnDesktop();
        desktop.addEventListener('change', closeOnDesktop);
        return () => desktop.removeEventListener('change', closeOnDesktop);
    }, [setNavbarOpen]);

    useLayoutEffect(() => {
        if (!navbarOpen) return;
        const previousOverflow = document.body.style.overflow;
        const page = document.getElementById('root');
        const wasInert = page?.hasAttribute('inert') ?? false;
        document.body.style.overflow = 'hidden';
        page?.setAttribute('inert', '');
        // Keep focus on the menu control; the first link is reached with Tab.
        toggleRef.current?.focus({preventScroll: true});

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                setNavbarOpen(false);
                toggleRef.current?.focus();
            }
            if (event.key !== 'Tab') return;
            const controls = [toggleRef.current, ...Array.from(menuRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') ?? [])]
                .filter((control): control is HTMLElement => control !== null);
            if (!controls.length) return;
            event.preventDefault();
            const current = controls.findIndex(control => control === document.activeElement);
            const next = (current + (event.shiftKey ? -1 : 1) + controls.length) % controls.length;
            controls[next].focus();
        };
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            page?.toggleAttribute('inert', wasInert);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [navbarOpen, setNavbarOpen]);

    // La navigation reste hors des conteneurs d'animation de route, dont les transformations
    // casseraient son `position: fixed`. `#chrome` est déclaré avant `#root` dans `index.html`
    // pour que l'ordre de tabulation suive l'ordre visuel ; `document.body` reste le repli si
    // le conteneur venait à manquer.
    return createPortal(
        <header className="site-header">
            {/* Le premier arrêt de tabulation de chaque page.

                Sans lui, atteindre le contenu au clavier demande de traverser la marque, les
                trois liens de navigation, le sélecteur de langue et le bouton du menu — à
                chaque page, et à chaque retour en arrière. Le lien reste hors écran jusqu'à ce
                qu'il reçoive le focus, où il se montre.

                Il vise `#contenu`, que chaque page pose sur son `<main>`. */}
            <a className="skip-link" href="#contenu">{t("nav.skip")}</a>
            <div className="navbar site-navbar">
                <Link to={localizePath("/")} className="site-brand" onClick={() => setNavbarOpen(false)} title={t("nav.home")}>
                    {restaurantInfo.name}
                </Link>
                <div className="navbar-actions">
                    <nav className="desktop-navigation" aria-label={t("nav.main")}>
                        <NavLink to={localizePath("/carte")}>{t("nav.menu")}</NavLink>
                        <NavLink to={localizePath("/galerie")}>{t("nav.photos")}</NavLink>
                        <Link to={localizePath("/#horaires")}>{t("nav.contact")}</Link>
                    </nav>
                    <LanguageSwitcher />
                    <button ref={toggleRef} type="button" className="navbar-toggle"
                        aria-label={navbarOpen ? t("nav.close") : t("nav.open")}
                        aria-expanded={navbarOpen} aria-controls={menuId}
                        onClick={() => setNavbarOpen(open => !open)}>
                        <span aria-hidden="true">{navbarOpen ? <Close/> : <ToggleMenu/>}</span>
                    </button>
                </div>
            </div>
            <div ref={menuRef} id={menuId} role="navigation" aria-label={t("nav.mobile")}
                aria-hidden={!navbarOpen} className={`mobile-navigation${navbarOpen ? ' is-open' : ''}`}>
                <div className="mobile-navigation-links">
                    <NavLink to={localizePath("/carte")} onClick={() => setNavbarOpen(false)}>{t("nav.menu")}</NavLink>
                    <Link to={localizePath("/#horaires")} onClick={() => setNavbarOpen(false)}>{t("nav.contact")}</Link>
                    <NavLink to={localizePath("/galerie")} onClick={() => setNavbarOpen(false)}>{t("nav.photos")}</NavLink>
                    <ReservationButton onClick={() => setNavbarOpen(false)} />
                </div>
                <SocialLinks networks={restaurantInfo.socialNetworks} />
            </div>
        </header>, document.getElementById('chrome') ?? document.body
    );
};

export default Navbar;
